// src/pages/finance-portal/inventory/Receiving.js
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Alert, Box, Button, Chip, FormControl, InputLabel, MenuItem,
    Paper, Select, Skeleton, Stack, Table, TableBody, TableCell,
    TableHead, TablePagination, TableRow, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import axiosInstance from '../../../utils/axiosInstance';
import {
    FONT_FAMILY, INV_ACCENT, INV_ACCENT_DARK,
    formatCurrency, formatDate,
    RECEIPT_STATUS_COLORS, INSPECTION_COLORS, statusChip,
} from './components/inventoryFormatters';

export default function Receiving() {
    const navigate = useNavigate();
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0);
    const [perPage, setPerPage] = useState(25);
    const [loading, setLoading] = useState(true);
    const [statusFilter, setStatusFilter] = useState('');

    const fetchReceipts = useCallback(async () => {
        setLoading(true);
        try {
            const params = { page: page + 1, per_page: perPage };
            if (statusFilter) params.status = statusFilter;
            const res = await axiosInstance.get('/api/finance/inventory/receipts', { params });
            if (res.data?.success) {
                setRows(res.data.data || []);
                setTotal(res.data.total || 0);
            }
        } finally {
            setLoading(false);
        }
    }, [page, perPage, statusFilter]);

    useEffect(() => { fetchReceipts(); }, [fetchReceipts]);

    return (
        <Box sx={{ p: 3, fontFamily: FONT_FAMILY, bgcolor: '#f8fafc', minHeight: '100vh' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                <Box>
                    <Typography sx={{ fontFamily: FONT_FAMILY, fontSize: '1.65rem', fontWeight: 700, color: '#0f172a' }}>
                        Receiving (GRN)
                    </Typography>
                    <Typography sx={{ fontFamily: FONT_FAMILY, fontSize: '0.9rem', color: '#64748b', mt: 0.5 }}>
                        Goods receipts with inspection (pass/fail/hold) and lot/serial capture
                    </Typography>
                </Box>
                <Button variant="contained" startIcon={<AddIcon />}
                    onClick={() => navigate('/finance-portal/inventory/receiving/new')}
                    sx={{ fontFamily: FONT_FAMILY, textTransform: 'none',
                          bgcolor: INV_ACCENT, '&:hover': { bgcolor: INV_ACCENT_DARK } }}>
                    New Receipt
                </Button>
            </Box>

            <Paper sx={{ p: 2, mb: 2, borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                <FormControl size="small" sx={{ minWidth: 200 }}>
                    <InputLabel>Status</InputLabel>
                    <Select value={statusFilter} label="Status"
                        onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}>
                        <MenuItem value="">All</MenuItem>
                        {['DRAFT', 'INSPECTING', 'POSTED', 'CANCELLED'].map((s) => (
                            <MenuItem key={s} value={s}>{s}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Paper>

            <Paper sx={{ borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                <Table>
                    <TableHead>
                        <TableRow sx={{ bgcolor: '#f1f5f9' }}>
                            <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }}>Receipt #</TableCell>
                            <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }}>PO</TableCell>
                            <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }}>Vendor</TableCell>
                            <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }}>Date</TableCell>
                            <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }}>Location</TableCell>
                            <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }}>Inspection</TableCell>
                            <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }} align="right">Value</TableCell>
                            <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }}>Status</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? Array.from({ length: 6 }).map((_, i) => (
                            <TableRow key={i}><TableCell colSpan={8}><Skeleton height={32} /></TableCell></TableRow>
                        )) : rows.length === 0 ? (
                            <TableRow><TableCell colSpan={8} sx={{ py: 6, textAlign: 'center', color: '#64748b' }}>
                                No receipts yet
                            </TableCell></TableRow>
                        ) : rows.map((r) => (
                            <TableRow key={r.id} hover
                                onClick={() => navigate(`/finance-portal/inventory/receiving/${r.id}`)}
                                sx={{ cursor: 'pointer' }}>
                                <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }}>{r.receipt_number}</TableCell>
                                <TableCell sx={{ fontFamily: FONT_FAMILY, fontSize: '0.85rem' }}>{r.purchase_order_number || '—'}</TableCell>
                                <TableCell sx={{ fontFamily: FONT_FAMILY }}>{r.vendor_name}</TableCell>
                                <TableCell sx={{ fontFamily: FONT_FAMILY, fontSize: '0.85rem' }}>{formatDate(r.receipt_date)}</TableCell>
                                <TableCell sx={{ fontFamily: FONT_FAMILY, fontSize: '0.85rem' }}>{r.received_at_location_name}</TableCell>
                                <TableCell>
                                    <Chip size="small" label={r.inspection_result?.replace('_', ' ')}
                                        style={statusChip(r.inspection_result, INSPECTION_COLORS)}
                                        sx={{ fontFamily: FONT_FAMILY, fontWeight: 600, fontSize: '0.7rem' }} />
                                </TableCell>
                                <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }} align="right">
                                    {formatCurrency(r.total_received_value, r.currency)}
                                </TableCell>
                                <TableCell>
                                    <Chip size="small" label={r.status}
                                        style={statusChip(r.status, RECEIPT_STATUS_COLORS)}
                                        sx={{ fontFamily: FONT_FAMILY, fontWeight: 600, fontSize: '0.7rem' }} />
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
                <TablePagination
                    component="div" count={total} page={page}
                    onPageChange={(e, p) => setPage(p)} rowsPerPage={perPage}
                    onRowsPerPageChange={(e) => { setPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                    sx={{ '.MuiTablePagination-toolbar': { fontFamily: FONT_FAMILY } }}
                />
            </Paper>
        </Box>
    );
}
